import { Link } from 'react-router-dom';
import { LINK_CLASS } from '@league/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';

import { Button, ErrorNote } from '../../components/ui';
import { ApiError, apiFetch, apiPost } from '../../lib/api';
import type { Actor } from '../../lib/auth';

/**
 * TASKS — ONE COMPONENT, BOTH SIDES, same reason as the notes thread.
 *
 * WHAT THE DIRECTOR SEES IS THE POINT. She gets one list: what HQ asked for and what the platform
 * noticed, together, because from where she sits they are the same job. Splitting them into "your
 * tasks" and "system alerts" would be organising the screen around who generated the row instead of
 * around what she has to do today.
 *
 * THE AUTO ONES CARRY A MARK AND NO CANCEL BUTTON. "The platform noticed" is worth saying — it
 * tells her nobody is checking up on her — and cancelling one would be arguing with the database:
 * the scores really are missing, and the next sweep would put it straight back.
 *
 * AND SHE MOSTLY WILL NOT TICK THEM. An auto task closes itself when the thing it describes stops
 * being true, which is what stops a task list becoming something people learn to ignore.
 */

interface TaskRow {
  id: string;
  title: string;
  detail: string | null;
  dueDate: string | null;
  status: string;
  autoGenerated: boolean;
  assigneeId: string | null;
  assigneeName: string | null;
  leagueId: string | null;
  leagueName: string | null;
  completedAt: string | null;
  overdue: boolean;
  action?: {href: string; label: string} | null;
}

interface Director {
  id: string;
  name: string | null;
}

function due(iso: string | null): string {
  if (!iso) return 'No date';
  return new Date(iso).toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

export function TasksPage({ actor }: { actor: Actor }) {
  const isCommissioner = actor.role === 'commissioner';
  const queryClient = useQueryClient();

  const [showDone, setShowDone] = useState(false);
  const [adding, setAdding] = useState(false);
  const [title, setTitle] = useState('');
  const [detail, setDetail] = useState('');
  const [dueDate, setDueDate] = useState('');
  const [assigneeId, setAssigneeId] = useState('');

  const list = useQuery({
    queryKey: ['tasks', showDone],
    queryFn: () =>
      apiFetch<{ tasks: TaskRow[]; directors: Director[] }>(
        `/api/tasks${showDone ? '?includeComplete=true' : ''}`,
      ),
  });

  const invalidate = async () => {
    await queryClient.invalidateQueries({ queryKey: ['tasks'] });
    await queryClient.invalidateQueries({ queryKey: ['hq'] });
  };

  const create = useMutation({
    mutationFn: (input: {
      title: string;
      detail: string | null;
      dueDate: string | null;
      assigneeId: string | null;
    }) => apiPost<{ task: TaskRow }>('/api/tasks', input),
    onSuccess: async () => {
      setAdding(false);
      setTitle('');
      setDetail('');
      setDueDate('');
      setAssigneeId('');
      await invalidate();
    },
  });

  const complete = useMutation({
    mutationFn: (id: string) => apiPost<{ task: TaskRow }>(`/api/tasks/${id}/complete`, {}),
    onSuccess: invalidate,
  });

  const cancel = useMutation({
    mutationFn: (id: string) => apiPost<{ task: TaskRow }>(`/api/tasks/${id}/cancel`, {}),
    onSuccess: invalidate,
  });

  if (list.isPending) return <p className="px-6 py-10 text-sm text-ink-subtle">Loading…</p>;

  const tasks = list.data?.tasks ?? [];
  const open = tasks.filter((task) => task.status === 'open');
  const overdue = open.filter((task) => task.overdue);
  const error = [create.error, complete.error, cancel.error].find((e) => e instanceof ApiError) as
    | ApiError
    | undefined;

  return (
    <div className="flex max-w-[52rem] flex-col gap-6 px-6 py-8">
      <header className="flex flex-wrap items-baseline justify-between gap-4">
        <div>
          <h1 className="font-display text-3xl leading-tight">
            Tasks
          </h1>
        </div>
        {isCommissioner && !adding ? (
          <Button variant="secondary" onClick={() => setAdding(true)}>
            Ask for something
          </Button>
        ) : null}
      </header>

      {error ? <ErrorNote>{error.message}</ErrorNote> : null}

      {adding ? (
        <section className="flex flex-col gap-3 rounded-md border border-border-subtle bg-surface px-5 py-4">
          <label className="block">
            <span className="text-[13px] font-medium">What do you need?</span>
            <input
              value={title}
              onChange={(event) => setTitle(event.target.value)}
              placeholder="What needs doing?"
              className="mt-2 h-11 w-full rounded-md border border-border-strong bg-bg px-3 text-[14px]"
            />
          </label>
          <label className="block">
            <span className="text-[13px] font-medium">Any detail</span>
            <textarea
              value={detail}
              onChange={(event) => setDetail(event.target.value)}
              rows={2}
              className="mt-2 w-full rounded-md border border-border-strong bg-bg px-3 py-2 text-[14px] leading-relaxed"
            />
          </label>
          <div className="flex flex-wrap items-end gap-3">
            <label className="block">
              <span className="text-[13px] font-medium">Who</span>
              <select
                value={assigneeId}
                onChange={(event) => setAssigneeId(event.target.value)}
                className="mt-2 h-11 rounded-md border border-border-strong bg-bg px-3 text-[13px]"
              >
                {/* A null assignee is not "unassigned", it is everybody — so it says so. */}
                <option value="">Every director</option>
                {(list.data?.directors ?? []).map((director) => (
                  <option key={director.id} value={director.id}>
                    {director.name ?? director.id}
                  </option>
                ))}
              </select>
            </label>
            <label className="block">
              <span className="text-[13px] font-medium">By when</span>
              <input
                type="date"
                value={dueDate}
                onChange={(event) => setDueDate(event.target.value)}
                className="tabular mt-2 h-11 rounded-md border border-border-strong bg-bg px-3 text-[14px]"
              />
            </label>
            <Button
              disabled={create.isPending || title.trim() === ''}
              onClick={() =>
                create.mutate({
                  title,
                  detail: detail.trim() || null,
                  dueDate: dueDate || null,
                  assigneeId: assigneeId || null,
                })
              }
            >
              Ask
            </Button>
            <Button variant="secondary" onClick={() => setAdding(false)}>
              Cancel
            </Button>
          </div>
        </section>
      ) : null}

      {overdue.length > 0 ? (
        <p className="rounded-md border border-negative bg-tint-negative px-5 py-3 text-[13px] text-negative">
          {overdue.length === 1
            ? 'One thing is past its date.'
            : `${overdue.length} things are past their date.`}
        </p>
      ) : null}

      {open.length === 0 && !showDone ? (
        <p className="rounded-md border border-dashed border-border-subtle px-5 py-10 text-center text-[14px] text-ink-subtle">
          {isCommissioner
            ? 'Nothing outstanding anywhere.'
            : 'Nothing outstanding. The platform will put something here if it spots a session with no scores.'}
        </p>
      ) : (
        <ul className="flex flex-col divide-y divide-border-subtle rounded-md border border-border-subtle bg-surface">
          {(showDone ? tasks : open).map((task) => {
            const done = task.status !== 'open';

            return (
              <li
                key={task.id}
                className={[
                  'flex flex-wrap items-start justify-between gap-4 px-5 py-4',
/*
                      OPACITY IS NOT A COLOUR SYSTEM. Dimming a whole row with `opacity` blends
                      every colour inside it toward the background, which quietly drags text under
                      the contrast bar — an axe pass found several at 3.3:1 that had been certified
                      at 4.5 in the tokens file. The token is still correct; the row made it a lie.

                      Dimmed with a token instead, so what the palette certifies is what renders.
                    */
                  done ? 'text-ink-subtle' : '',
                  task.overdue ? 'bg-tint-negative' : '',
                ].join(' ')}
              >
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-baseline gap-2">
                    <span
                      className={[
                        'text-[14px] font-medium',
                        task.overdue ? 'text-negative' : '',
                      ].join(' ')}
                    >
                      {task.title}
                    </span>
                  </div>
                  {task.detail ? (
                    <p className="mt-1 text-[13px] leading-relaxed text-ink-muted">{task.detail}</p>
                  ) : null}
                  <p className="mt-1.5 text-[12px] text-ink-muted">
                    {isCommissioner ? `${task.assigneeName ?? 'Every director'} · ` : ''}
                    {due(task.dueDate)}
                    {task.leagueName ? ` · ${task.leagueName}` : ''}
                    {done ? ` · ${task.status === 'complete' ? 'Done' : 'Called off'}` : ''}
                  </p>
                </div>

                {!done ? (
                  <div className="flex shrink-0 items-center gap-2">
                    {task.action ? <Link className={`text-sm ${LINK_CLASS}`} to={task.action.href}>{task.action.label}</Link> : null}
                    {!task.autoGenerated ? <Button variant="secondary" size="inline" disabled={complete.isPending} onClick={() => complete.mutate(task.id)}>Done</Button> : null}
                    {/*
                      No cancel on an auto task. It would be arguing with the database — the next
                      sweep puts it straight back, and a button that undoes itself is worse than
                      no button.
                    */}
                    {isCommissioner && !task.autoGenerated ? (
                      <Button
                        variant="destructive"
                        size="inline"
                        disabled={cancel.isPending}
                        onClick={() => cancel.mutate(task.id)}
                      >
                        Never mind
                      </Button>
                    ) : null}
                  </div>
                ) : null}
              </li>
            );
          })}
        </ul>
      )}

      <label className="flex items-center gap-2 text-[13px] text-ink-muted">
        <input
          type="checkbox"
          checked={showDone}
          onChange={(event) => setShowDone(event.target.checked)}
        />
        Show what is already done
      </label>
    </div>
  );
}
